import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { POST, GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import SimpleModal from "../../common/SimpleModal/SimpleModal";
import warningIcon from "../../resources/icons/warning-info.svg";
import CreateOrganisationModal from "../../common/CreateOrganisationModal/CreateOrganisationModal";
import EntityConstants from "../../configs/EntityConstants";
import CreateTenantUserModal from "../../common/CreateTenantUserModal/CreateTenantUserModal";
import { ChevronRight, Ban } from "lucide-react";
import useSearchDebounce from "../../hooks/useSearchDebounce";
import SimpleToggleButton from "../../common/SimpleToggleButton/SimpleToggleButton";
import AuthContext from "../../store/auth-context";

const AdminUserOrganisation = () => {
  const { tenant_code } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const [organisations, setOrganisations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgNext, setOrgNext] = useState(null);
  const [orgPrevious, setOrgPrevious] = useState(null);
  const [orgLimit] = useState(6);
  const [orgOffset, setOrgOffset] = useState(0);
  const [updatingOrgCode, setUpdatingOrgCode] = useState(null);
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [isOrgSearching, setIsOrgSearching] = useState(false);
  const [orgToggleRefresh, setOrgToggleRefresh] = useState(false);
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(true);
  const [userNext, setUserNext] = useState(null);
  const [userPrevious, setUserPrevious] = useState(null);
  const [userLimit] = useState(6);
  const [userOffset, setUserOffset] = useState(0);
  const [updatingUserEmail, setUpdatingUserEmail] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [isUserSearching, setIsUserSearching] = useState(false);
  const [userToggleRefresh, setUserToggleRefresh] = useState(false);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    type: "",
    item: null,
    currentStatus: false,
  });

  const isTenantAdmin = authContext.userType?.toLowerCase() === "tenant_admin";
  const isTenantUser = authContext.userType?.toLowerCase() === "tenant_user";
  const isSuperAdmin = authContext.userType?.toLowerCase() === "super_admin";

  const hasTenantAdmin = users.some(
    (user) => user.user_type?.toLowerCase() === "tenant_admin"
  );

  const hasTenantUser = users.some(
    (user) => user.user_type?.toLowerCase() === "tenant_user"
  );

  const performOrgSearchAPI = async (searchTerm, signal) => {
    try {
      const endpoint = `${EntityConstants.GET_TENANT_ORGANISATIONS}?tenant_code=${tenant_code}&limit=${orgLimit}&offset=0&search=${searchTerm}`;
      const response = await GET(endpoint, { signal });
      const data = await response.json();

      if (data.success) {
        setOrganisations(data.data.results || []);
        setOrgNext(data.data.next);
        setOrgPrevious(data.data.previous);
        setOrgOffset(0);
      } else {
        Toast({
          type: "error",
          title: "Error",
          message: data.message || "Failed to search organisations",
        });
        setOrganisations([]);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Search organisations error:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to search organisations",
        });
        setOrganisations([]);
      }
    }
  };

  const { handleSearch: handleOrgSearch } = useSearchDebounce(
    performOrgSearchAPI,
    500
  );

  const performUserSearchAPI = async (searchTerm, signal) => {
    try {
      const endpoint = `${EntityConstants.GET_TENANT_USERS}?tenant_code=${tenant_code}&limit=${userLimit}&offset=0&search=${searchTerm}`;
      const response = await GET(endpoint, { signal });
      const data = await response.json();

      if (data.success) {
        setUsers(data.data.results || []);
        setUserNext(data.data.next);
        setUserPrevious(data.data.previous);
        setUserOffset(0);
      } else {
        Toast({
          type: "error",
          title: "Error",
          message: data.message || "Failed to search users",
        });
        setUsers([]);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Search users error:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to search users",
        });
        setUsers([]);
      }
    }
  };

  const { handleSearch: handleUserSearch } = useSearchDebounce(
    performUserSearchAPI,
    500
  );

  const fetchOrganisations = async (
    offset = 0,
    search = "",
    isToggle = false
  ) => {
    if (!isToggle) {
      setOrgLoading(true);
    }
    try {
      const endpoint = search
        ? `${EntityConstants.GET_TENANT_ORGANISATIONS}?tenant_code=${tenant_code}&limit=${orgLimit}&offset=${offset}&search=${search}`
        : `${EntityConstants.GET_TENANT_ORGANISATIONS}?tenant_code=${tenant_code}&limit=${orgLimit}&offset=${offset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (data.success) {
        setOrganisations(data.data.results || []);
        setOrgNext(data.data.next);
        setOrgPrevious(data.data.previous);
        setOrgOffset(offset);
      } else {
        Toast({
          type: "error",
          title: "Error",
          message: data.message || "Failed to fetch organisations",
        });
      }
    } catch (error) {
      console.error("Error fetching organisations:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch organisations",
      });
    } finally {
      setOrgLoading(false);
      if (isToggle) {
        setOrgToggleRefresh(false);
      }
    }
  };

  const fetchUsers = async (offset = 0, search = "", isToggle = false) => {
    if (!isToggle) {
      setUserLoading(true);
    }
    try {
      const endpoint = search
        ? `${EntityConstants.GET_TENANT_USERS}?tenant_code=${tenant_code}&limit=${userLimit}&offset=${offset}&search=${search}`
        : `${EntityConstants.GET_TENANT_USERS}?tenant_code=${tenant_code}&limit=${userLimit}&offset=${offset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (data.success) {
        setUsers(data.data.results || []);
        setUserNext(data.data.next);
        setUserPrevious(data.data.previous);
        setUserOffset(offset);
      } else {
        Toast({
          type: "error",
          title: "Error",
          message: data.message || "Failed to fetch users",
        });
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch users",
      });
    } finally {
      setUserLoading(false);
      if (isToggle) {
        setUserToggleRefresh(false);
      }
    }
  };

  useEffect(() => {
    if (tenant_code) {
      fetchOrganisations(0);
      fetchUsers(0);
    }
  }, [tenant_code]);

  const handleOrgToggle = (org) => {
    setConfirmModal({
      show: true,
      type: "org",
      item: org,
      currentStatus: org.is_active,
    });
  };

  const handleOrgRowClick = (org) => {
    navigate(
      `/issuer/tenant/${org.tenant_code}/organisations/${org.org_code}/organisations`
    );
  };

  const handleUserToggle = (user) => {
    setConfirmModal({
      show: true,
      type: "user",
      item: user,
      currentStatus: user.is_active,
    });
  };

  const confirmToggle = async () => {
    const { type, item, currentStatus } = confirmModal;

    if (!item) {
      cancelToggle();
      return;
    }

    if (type === "org") {
      setUpdatingOrgCode(item.org_code);
      try {
        const payload = {
          org_code: item.org_code,
          status: !currentStatus,
        };

        const response = await POST(
          EntityConstants.POST_TENANT_UPDATE_ORGANISATION,
          payload
        );

        const data = await response.json();

        if (data.success) {
          Toast({
            type: "success",
            title: "Success",
            message: data.message || "Organisation status updated successfully",
          });
          setOrgToggleRefresh(true);
          fetchOrganisations(orgOffset, orgSearchTerm, true);
        } else {
          Toast({
            type: "error",
            title: "Error",
            message: data.message || "Failed to update organisation status",
          });
        }
      } catch (error) {
        console.error("Error updating organisation:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to update organisation status",
        });
      } finally {
        setUpdatingOrgCode(null);
      }
    } else if (type === "user") {
      setUpdatingUserEmail(item.email);
      try {
        const payload = {
          email: item.email,
          status: !currentStatus,
          tenant_code: tenant_code,
        };

        const response = await POST(
          `${EntityConstants.POST_TENANT_UPDATE_USER}`,
          payload
        );

        const data = await response.json();

        if (data.success) {
          Toast({
            type: "success",
            title: "Success",
            message: data.message || "User status updated successfully",
          });
          setUserToggleRefresh(true);
          fetchUsers(userOffset, userSearchTerm, true);
        } else {
          Toast({
            type: "error",
            title: "Error",
            message: data.message || "Failed to update user status",
          });
        }
      } catch (error) {
        console.error("Error updating user:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to update user status",
        });
      } finally {
        setUpdatingUserEmail(null);
      }
    }

    setConfirmModal({
      show: false,
      type: "",
      item: null,
      currentStatus: false,
    });
  };

  const cancelToggle = () => {
    setConfirmModal({
      show: false,
      type: "",
      item: null,
      currentStatus: false,
    });
  };

  const handleOrgPrevious = () => {
    if (orgPrevious !== null) {
      const prevOffset = Math.max(0, orgOffset - orgLimit);
      fetchOrganisations(prevOffset, orgSearchTerm);
    }
  };

  const handleOrgNext = () => {
    if (orgNext !== null) {
      const nextOffset = orgOffset + orgLimit;
      fetchOrganisations(nextOffset, orgSearchTerm);
    }
  };

  const handleUserPrevious = () => {
    if (userPrevious !== null) {
      const prevOffset = Math.max(0, userOffset - userLimit);
      fetchUsers(prevOffset, userSearchTerm);
    }
  };

  const handleUserNext = () => {
    if (userNext !== null) {
      const nextOffset = userOffset + userLimit;
      fetchUsers(nextOffset, userSearchTerm);
    }
  };

  const handleOrgCreated = () => {
    fetchOrganisations(orgOffset, orgSearchTerm);
  };

  const handleUserCreated = () => {
    fetchUsers(userOffset, userSearchTerm);
  };

  const handleOrgSearchInputChange = (e) => {
    const value = e.target.value ? e.target.value : "";
    setOrgSearchTerm(value);
    setOrgOffset(0);

    if (value.length > 0) {
      setIsOrgSearching(true);
      handleOrgSearch(value, {
        onStart: () => setIsOrgSearching(true),
        onComplete: () => setIsOrgSearching(false),
        onError: () => {
          setOrganisations([]);
          setIsOrgSearching(false);
        },
        minChars: 1,
      });
    } else {
      setIsOrgSearching(false);
      fetchOrganisations(0, "");
    }
  };

  const handleUserSearchInputChange = (e) => {
    const value = e.target.value ? e.target.value : "";
    setUserSearchTerm(value);
    setUserOffset(0);

    if (value.length > 0) {
      setIsUserSearching(true);
      handleUserSearch(value, {
        onStart: () => setIsUserSearching(true),
        onComplete: () => setIsUserSearching(false),
        onError: () => {
          setUsers([]);
          setIsUserSearching(false);
        },
        minChars: 1,
      });
    } else {
      setIsUserSearching(false);
      fetchUsers(0, "");
    }
  };

  const handleTenantClick = () => {
    navigate("/issuer/tenant");
  };

  const OrganisationRow = ({ org, index }) => {
    return (
      <div
        key={index}
        className="flex flex-row items-center border rounded-[var(--cf-issued-credential-border-radius)] py-[var(--cf-issued-credential-template-card-padding-y)] px-6 hover:bg-[#F9FAFB] min-h-[5rem] min-w-[78.8rem] cursor-pointer"
        onClick={() => handleOrgRowClick(org)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOrgRowClick(org);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="w-1/5 text-center min-w-[200px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText font-semibold whitespace-nowrap truncate">
            {org.org_name}
          </p>
        </div>
        <div className="w-1/5 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText truncate">{org.org_code}</p>
        </div>
        <div className="w-1/5 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText truncate">{org.org_type || "N/A"}</p>
        </div>
        <div className="w-1/5 text-center min-w-[120px] flex-shrink-0 overflow-hidden">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              org.is_active
                ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG text-cfIssuedCredentialTemplateCardStatusText"
                : "bg-cfIssuedCredentialTemplateCardStatusRevokedBG text-white"
            }`}
          >
            {org.is_active ? "Active" : "Inactive"}
          </span>
        </div>
        <div className="w-1/5 text-center min-w-[120px] flex-shrink-0 overflow-hidden">
          <div className="relative inline-flex items-center cursor-pointer">
            <SimpleToggleButton
              checked={org.is_active}
              onChange={() => handleOrgToggle(org)}
              disabled={updatingOrgCode === org.org_code}
            />
          </div>
        </div>
      </div>
    );
  };

  const UserRow = ({ user, index }) => {
    return (
      <div
        key={index}
        className="flex flex-row items-center border rounded-[var(--cf-issued-credential-border-radius)] py-[var(--cf-issued-credential-template-card-padding-y)] px-6 hover:bg-[#F9FAFB] min-h-[5rem] min-w-[78.8rem]"
      >
        <div className="w-1/5 text-center min-w-[200px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText font-semibold whitespace-nowrap truncate">
            {user.user_name ||
              `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
              "N/A"}
          </p>
        </div>
        <div className="w-1/5 text-center min-w-[200px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText truncate">{user.email}</p>
        </div>
        <div className="w-1/5 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
          <p className="text-cfHeadingText truncate">{user.user_type}</p>
        </div>
        <div className="w-1/5 text-center min-w-[120px] flex-shrink-0 overflow-hidden">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              user.is_active
                ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG text-cfIssuedCredentialTemplateCardStatusText"
                : "bg-cfIssuedCredentialTemplateCardStatusRevokedBG text-white"
            }`}
          >
            {user.is_active ? "Active" : "Inactive"}
          </span>
        </div>
        <div className="w-1/5 text-center min-w-[120px] flex-shrink-0 overflow-hidden">
          <div className="relative inline-flex items-center cursor-pointer">
            <SimpleToggleButton
              checked={user.is_active}
              onChange={() => handleUserToggle(user)}
              disabled={updatingUserEmail === user.email}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center w-full pb-3 pt-20 bg-cfSecondary min-h-screen">
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[1.4rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
        >
          <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
            <h2 className="text-lg font-medium flex items-center justify-center w-full">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                {!(isTenantAdmin || isTenantUser) ? (
                  <>
                    <button
                      onClick={handleTenantClick}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleTenantClick();
                        }
                      }}
                      className="hover:text-gray-800 cursor-pointer focus:outline-none"
                    >
                      Tenant Management
                    </button>
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                    <span className="text-gray-800 font-medium">
                      Admin Management
                    </span>
                  </>
                ) : (
                  <div className="text-cfHeadingText font-semibold">
                    Admin Management
                  </div>
                )}
              </div>
            </h2>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col mt-4 px-4">
        {!isSuperAdmin && (
          <div className="mb-8">
            <div className="flex flex-col md:flex-row sm:flex-row justify-between items-start sm:items-center mb-4">
              <h2 className="text-lg font-semibold text-cfHeadingText">
                Tenant Organisations
              </h2>
              <div className="flex flex-col md:flex-row items-start gap-3">
                <button
                  onClick={() => setShowCreateOrgModal(true)}
                  className={`px-4 py-2 bg-[#3538CD] text-white font-medium rounded-md hover:bg-blue-700 transition-colors duration-200`}
                >
                  Create Organisation
                </button>

                <input
                  type="text"
                  name="search"
                  id="search"
                  placeholder="Search for organisations by name or code"
                  className="outline-none border border-gray-300 px-2 py-[2rem] rounded-lg placeholder:text-sm"
                  style={{
                    width: "350px",
                    fontSize: "0.875rem",
                    lineHeight: "1.4rem",
                  }}
                  value={orgSearchTerm}
                  onChange={handleOrgSearchInputChange}
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <div className="flex flex-col justify-center gap-2 min-w-full">
                <div className="flex bg-[#E0EAFF] items-center min-h-[4.5rem] border text-center px-6 font-bold text-[14px] min-w-[78.8rem]">
                  <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                    Organisation Name
                  </div>
                  <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                    Code
                  </div>
                  <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                    Type
                  </div>
                  <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                    Status
                  </div>
                  <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                    Action
                  </div>
                </div>

                {isOrgSearching && !orgToggleRefresh ? (
                  <div className="flex justify-center items-center min-h-[8rem]">
                    <CfLoader />
                  </div>
                ) : orgLoading ? (
                  <div className="flex justify-center items-center min-h-[8rem]">
                    <CfLoader />
                  </div>
                ) : organisations.length === 0 ? (
                  <div className="flex justify-center items-center w-full py-3 bg-cfSecondary text-gray-600 font-semibold text-lg">
                    No organisations found
                  </div>
                ) : (
                  organisations.map((org) => (
                    <OrganisationRow key={org.org_code} org={org} />
                  ))
                )}
              </div>
            </div>

            <div className="flex items-center justify-between mt-6 w-full">
              <button
                onClick={handleOrgPrevious}
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={orgPrevious === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                    />
                  </svg>
                  <span>previous</span>
                </span>
              </button>

              <button
                onClick={handleOrgNext}
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={orgNext === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <span>Next</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        )}
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
            <h2 className="text-lg font-semibold text-cfHeadingText">
              {isSuperAdmin ? "Tenant Admin" : "Tenant Users"}
            </h2>
            <div className="flex flex-col md:flex-row items-start gap-3">
              <div className="relative group">
                <div className="relative inline-block group">
                  <button
                    disabled={isSuperAdmin && (hasTenantAdmin || hasTenantUser)}
                    onClick={() => {
                      if (isSuperAdmin && (hasTenantAdmin || hasTenantUser))
                        return;
                      setShowCreateUserModal(true);
                    }}
                    className={`px-4 py-2 font-medium rounded-md transition-colors duration-200 ${
                      isSuperAdmin && (hasTenantAdmin || hasTenantUser)
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-[#3538CD] text-white hover:bg-blue-700 cursor-pointer"
                    }`}
                  >
                    {isSuperAdmin
                      ? "Create Tenant Admin"
                      : "Create Tenant Users"}
                  </button>
                  {isSuperAdmin && (hasTenantAdmin || hasTenantUser) && (
                    <span className="absolute -top-2 -right-2 hidden group-hover:block">
                      <Ban className="w-5 h-5 text-red-500" />
                    </span>
                  )}
                </div>

                {isSuperAdmin && (hasTenantAdmin || hasTenantUser) && (
                  <div
                    className="absolute z-10 hidden group-hover:block
                              top-full mt-2 left-1/2 -translate-x-1/2
                              bg-gray-800 text-white text-xs rounded-md px-3 py-1
                              whitespace-nowrap shadow-lg"
                  >
                    Only one Tenant Admin is allowed per tenant.
                  </div>
                )}
              </div>

              <input
                type="text"
                name="search"
                id="search"
                placeholder="Search for tenant admins name or email"
                className="outline-none border border-gray-300 px-2 py-[2rem] rounded-lg placeholder:text-sm"
                style={{
                  width: "350px",
                  fontSize: "0.875rem",
                  lineHeight: "1.4rem",
                }}
                value={userSearchTerm}
                onChange={handleUserSearchInputChange}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="flex flex-col justify-center gap-2 min-w-full">
              <div className="flex bg-[#E0EAFF] items-center min-h-[4.5rem] border text-center min-w-[78.8rem] px-6 font-bold text-[14px]">
                <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                  Name
                </div>
                <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                  Email
                </div>
                <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                  User Type
                </div>
                <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                  Status
                </div>
                <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/5">
                  Action
                </div>
              </div>

              {isUserSearching && !userToggleRefresh ? (
                <div className="flex justify-center items-center min-h-[8rem]">
                  <CfLoader />
                </div>
              ) : userLoading ? (
                <div className="flex justify-center items-center min-h-[8rem]">
                  <CfLoader />
                </div>
              ) : users.length === 0 ? (
                <div className="flex justify-center items-center w-full py-3 bg-cfSecondary text-gray-600 font-semibold text-lg">
                  No users found
                </div>
              ) : (
                users.map((user) => <UserRow key={user.email} user={user} />)
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 w-full">
            <button
              onClick={handleUserPrevious}
              className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
              disabled={userPrevious === null}
            >
              <span className="flex items-center justify-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 rtl:-scale-x-100"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                  />
                </svg>
                <span>previous</span>
              </span>
            </button>

            <button
              onClick={handleUserNext}
              className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
              disabled={userNext === null}
            >
              <span className="flex items-center justify-center gap-2">
                <span>Next</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 rtl:-scale-x-100"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>

      <CreateOrganisationModal
        isOpen={showCreateOrgModal}
        onClose={() => setShowCreateOrgModal(false)}
        onOrganisationCreated={handleOrgCreated}
        tenantCode={tenant_code}
      />

      <CreateTenantUserModal
        isOpen={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        onUserCreated={handleUserCreated}
      />

      <SimpleModal
        isOpen={confirmModal.show}
        closeModal={cancelToggle}
        successFunction={confirmToggle}
        modalIcon={warningIcon}
        modalHeaderMessage={`${
          confirmModal.currentStatus ? "Deactivate" : "Activate"
        } ${confirmModal.type === "org" ? "Organisation" : "User"}`}
        modalBodyMessage={`Are you sure you want to ${
          confirmModal.currentStatus ? "deactivate" : "activate"
        } "${
          confirmModal.type === "org"
            ? confirmModal.item?.org_name
            : confirmModal.item?.first_name + " " + confirmModal.item?.last_name
        }"?`}
        leftButtonMessage="Cancel"
        rightButtonMessage="Confirm"
      />
    </div>
  );
};

export default AdminUserOrganisation;
